/** Multiplayer: the room browser, the lobby room, network status and server messages. */
export const mp = {
  'mp.kicker': 'Signals net · Multiplayer',
  'mp.title': 'Join the War Council',
  'mp.commander': 'Commander',
  'mp.commander.hint': 'Shown to the other players. Remembered on this machine.',
  'mp.commander.placeholder': 'e.g. Gen. Hartmann',
  'mp.status.online': 'CONNECTED',
  'mp.status.onlinePing': 'CONNECTED · {ping} ms',
  'mp.status.connecting': 'ESTABLISHING LINE…',
  'mp.status.offline': 'OFFLINE',
  'mp.connect': 'Connect',
  'mp.disconnect': 'Disconnect',
  'mp.openRooms': 'Open rooms',
  'mp.refresh': 'Refresh',
  'mp.noLine.title': 'No line to headquarters',
  'mp.noLine.text': 'Enter your name and connect to see the rooms on the signals net.',
  'mp.empty.title': 'The war council is empty',
  'mp.empty.text': 'Create a room and send the five-letter code to your opponents.',
  'mp.col.code': 'Code',
  'mp.col.room': 'Room',
  'mp.col.players': 'Players',
  'mp.col.theatre': 'Theatre',
  'mp.col.state': 'State',
  'mp.state.lobby': 'Forming',
  'mp.state.running': 'In battle',
  'mp.state.finished': 'Finished',
  'mp.join': 'Join',
  'mp.rejoin': 'Rejoin',
  'mp.joinByCode': 'Join by code',
  'mp.code.aria': 'Room code',
  'mp.joinRoom': 'Join room',
  'mp.newRoom': 'Open a new room',
  'mp.roomName': 'Room name',
  'mp.roomName.default': '{name}’s war',
  'mp.roomName.placeholder': 'The Varnian Marches',
  'mp.theatre': 'Theatre',
  'mp.create': 'Create room',
  'mp.hostNote': 'You become the host: you set the rules, fill slots with AI and start the war.',

  // ---- lobby room
  'lobby.kicker': 'War council · {n} factions',
  'lobby.kickerPing': 'War council · {n} factions · {ping} ms',
  'lobby.leave': 'Leave',
  'lobby.code.copy': 'Copy room code',
  'lobby.code.label': 'Room',
  'lobby.code.copied': 'Copied',
  'lobby.invite': 'Copy invite link',
  'lobby.invite.copied': 'Link copied',
  'lobby.ctl.open': 'Open',
  'lobby.ctl.ai': 'Computer',
  'lobby.ctl.closed': 'Closed',
  'lobby.commander': 'Commander',
  'lobby.host': 'HOST',
  'lobby.human': 'Human',
  'lobby.dropped': 'Disconnected — AI in command',
  'lobby.computer': 'Computer',
  'lobby.openSlot': 'Open slot',
  'lobby.closedSlot': 'Closed',
  'lobby.waitingCommander': 'Waiting for a commander',
  'lobby.notInPlay': 'Not in play',
  'lobby.changeColour': 'Change colour',
  'lobby.player': 'Player',
  'lobby.takeSlot': 'Take slot',
  'lobby.take': 'Take',
  'lobby.moveHere': 'Move to this slot',
  'lobby.ready': 'Ready',
  'lobby.waiting': 'Waiting',
  'lobby.standingBy': 'Standing by',
  'lobby.rules': 'Rules',
  'lobby.rulesByHost': 'Rules · set by the host',
  'lobby.roomName': 'Room name',
  'lobby.theatre': 'Theatre',
  'lobby.seed': 'Seed',
  'lobby.dayLimit': 'Day limit',
  'lobby.speed': 'Speed',
  'lobby.decisive': 'Decisive victory',
  'lobby.decisiveVal': '{pct} VP · 24 h',
  'lobby.decisiveRo': '{pct} of all VP held for 24 h',
  'lobby.season': 'Season',
  'lobby.techs': 'Starting technology',
  'lobby.chat.placeholder': 'Message the council…',
  'lobby.chat.send': 'Send',
  'lobby.belligerents': 'Belligerents',
  'lobby.chatter': 'Council chatter',
  'lobby.needTwo': 'At least two factions are needed.',
  'lobby.oneTeam': 'Everyone is on one team.',
  'lobby.notReady': 'Waiting for players to report ready.',
  'lobby.hostNote': 'Share the invite link or the code. You start the war when everyone is ready.',
  'lobby.guestNote': 'The host starts the war once everyone reports ready.',
  'lobby.notReadyBtn': 'Not ready',
  'lobby.reportReady': 'Report ready',
  'lobby.start': 'Start the war',
  'lobby.scenario': 'Scenario',
  'lobby.scenario.none': 'None — free setup',
  'lobby.scenario.hint':
    'A situation from history: it sets the theatre, the season, the terms and every side’s forces.',
  'lobby.scenario.fixed': 'Set by the scenario',
  'lobby.scn.lead': 'Briefed side',
  'lobby.scn.leadHint': 'The briefing is written for this side.',
  'lobby.scn.outside': 'Not in this scenario',
  'lobby.scn.ffa': 'Every nation for itself',

  // ---- signals line (in-game banner)
  'net.reconnecting': 'Signals line down — re-establishing contact with headquarters…',

  // ---- errors raised by the client itself
  'err.version': 'This client is out of date with the server. Reload the page.',
  'err.replaced': 'You connected from another window or tab.',
  'err.rateLimited': 'Disconnected for sending too many messages.',
  'err.tooBig': 'Disconnected: a message was too large.',
  'err.policy': 'Disconnected by the server.',
  'err.lost': 'Lost connection to the server.',
  'err.lostReason': 'Lost connection to the server ({reason}).',
  'err.unreachable': 'The server is unreachable.',
  'err.invalidUrl': 'Invalid server URL',
  'err.simStopped': 'The simulation stopped: {detail}',
  'err.spError': 'Single-player error: {detail}',
  'err.spCrashed': 'The single-player engine crashed: {detail}',
  'err.spUnreadable': 'The single-player engine sent an unreadable message.',
  'err.replay': 'The replay could not be played.',
  'err.replayCrashed': 'The replay engine crashed.',

  // ---- server messages (the server words them in English; see net/serverText.ts)
  'srv.hello': 'Say hello first.',
  'srv.notInRoom': 'You are not in a room.',
  'srv.greeted': 'Already greeted.',
  'srv.busy': 'Too many wars are being fought right now. Try again later.',
  'srv.full': 'The server is full. Try again later.',
  'srv.noRoom': 'No room with that code.',
  'srv.begun': 'That war has already begun.',
  'srv.theBegun': 'The war has already begun.',
  'srv.roomFull': 'The room is full.',
  'srv.noSlot': 'No such slot.',
  'srv.hostOnlySlots': 'Only the host can change other slots.',
  'srv.hostOnlyControl': 'Only the host can change slot control.',
  'srv.takeYourself': 'Players take open slots themselves.',
  'srv.occupied': 'That slot is occupied.',
  'srv.colour': 'That colour is already taken.',
  'srv.notOpen': 'That slot is not open.',
  'srv.noSeat': 'You have no seat in this room.',
  'srv.tooQuick': 'You are sending messages too quickly.',
  'srv.twoSides': 'A war needs at least two sides.',
  'srv.sameTeam': 'All sides are on the same team.',
  'srv.notConnected': '{name} is not connected.',
  'srv.notReady': '{name} is not ready.',
  'srv.aPlayer': 'A player',
  'srv.generate': 'The server could not generate this war. Try another seed.',
  'srv.noWar': 'No war is being fought here.',
  'srv.gone': 'That room no longer exists.',
  'srv.hostOnly': 'Only the host can do that.',
  'srv.otherWindow': 'You connected from another window.',
  'srv.internal': 'Internal server error.',
  'srv.aborted': 'The match was aborted by a server error.',
  'srv.outdated': 'Your client is out of date with this server. Reload the page.',
  'srv.scnSides': 'This scenario has {n} sides.',
  'srv.scnPlayed': 'Every side of the scenario must be played.',
  'srv.scnAlliances': 'The scenario sets the alliances.',
  'srv.scnRoom': 'This scenario has room for {n} commanders.',
  'srv.mapSides': 'This map has {n} nations.',
  'srv.mapPlayed': 'Every nation on this map must be played.',
  'srv.mapRoom': 'This map has room for {n} commanders.',
  'srv.observeAlive': 'Only a nation that has capitulated may observe the war.',
  'srv.observeAlliance': 'In a war of alliances a defeated nation keeps its own staff map.',
  'srv.noSeatWar': 'You have no seat in this war.',
  'srv.noPerspective': 'There is no such perspective.',
  'srv.code.version': 'The client is out of date. Reload the page.',
  'srv.code.serverFull': 'The server is full. Try again later.',
  'srv.code.notFound': 'Not found.',
  'srv.code.roomFull': 'The room is full.',
  'srv.code.forbidden': 'Not permitted.',
  'srv.code.state': 'Not possible at this moment.',
  'srv.code.invalid': 'Invalid request.',
  'srv.code.rateLimited': 'Too many requests — slow down.',
  'srv.code.internal': 'Server error.',
  // system lines in the room chat
  'chat.joined': '{name} joined.',
  'chat.left': '{name} left.',
  'chat.removed': '{name} was removed.',
  'chat.lost': '{name} lost connection.',
  'chat.back': '{name} reconnected.',
  'chat.begun': 'The war has begun.',
  'chat.over': 'The war is over.',
  'chat.overVictory': 'The war is over. Victory: {names}.',
  'chat.aborted': 'The match was aborted by a server error.',
  'chat.newHost': '{name} is now the host.',
  // commands the host rejected (the engine words them in English; see net/serverText.ts)
  'cmd.rejected': 'Order not accepted — {reason}',
  'cmd.noUnits': 'no units selected',
  'cmd.maxUnits': 'at most {n} units per order',
  'cmd.outside': 'target outside the map',
  'cmd.impassable': 'target is impassable',
  'cmd.hHour': 'invalid H-hour',
  'cmd.noRadio': 'Field Radio not researched',
  'cmd.notYours': 'unit {id} is not under your command',
  'cmd.noHq': 'no headquarters or city to issue orders from',
  'cmd.requires': 'requires {tech}',
  'cmd.notBuildable': 'cannot be produced',
  'cmd.cityLost': 'city not under your control',
  'cmd.queueFull': 'production queue full',
  'cmd.manpower': 'not enough manpower',
  'cmd.researched': 'already researched',
  'cmd.over': 'the match is over',
  'cmd.capitulated': 'your nation has capitulated',
  'cmd.noItem': 'no such production item',
  'cmd.other': 'the host refused it',
} as const;
